import {
  FormGroup,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  useTheme,
} from '@mui/material';
import {
  CcfBackIcon,
  CcfBox,
  CcfButton,
  CcfDatePickerIcon,
  CcfDivider,
  CcfTextField,
  CcfTypography,
  DividerOrientation,
  DividerVariant,
  useTranslator,
  CcfRadioGroup,
  CcfIconButton,
} from '@nice-ccf/ui-controls';
import { useSelector, useDispatch } from 'react-redux';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import React, { useMemo, useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import ccfCommitmentStyles from './ccf-commitment-form.styles';
import { isValidPhoneNumber } from './../../util/uiValidationUtils';

import {
  commitmentActions,
  getAddCommitmentByCall,
  getCommitmentPermission,
  getFormData,
  getIsEditCommitment,
  getIsRescheduleCommitment,
  getRemoveCommitmentSettings,
  getTimeZones
} from './ccf-commitment.slice';

import { CallType, CommitmentRequest } from '@nice-ccf/agent-sdk';
import { useRemoveCommitment } from './ccf-remove-commitment';
import { getApplicationLocale } from '../global.app.slice';
import { getContacts } from '../ccf-inbox/ccf-inbox.slice';

export interface phoneOBSkills 
{ skillId: number; skillName: string }

/**
 * DatePickerIcon used to display date picker icon
 * @example <DatePickerIcon />
 */
const DatePickerIcon = () => {
  return (
    <CcfBox sx={{ display: 'flex' }}>
      <CcfDatePickerIcon />
    </CcfBox>
  );
};

/**
 * CcfCommitmentsForm used to display commitment form
 * @example <CcfCommitmentsForm />
 */
export const CcfCommitmentsForm = (props: { isFullView?: boolean }) => {
  const { isFullView } = props;
  const [translate] = useTranslator();
  const theme = useTheme();
  const dispatch = useDispatch();
  const classes = ccfCommitmentStyles(theme);
  const [validateForm, setValidateForm] = useState({
    fname: '',
    lname: '',
    phone: '',
    dateTime: '',
  });

  const formState = useSelector(getFormData);
  const activeContacts = useSelector(getContacts);
  const addCommitmentByCall = useSelector(getAddCommitmentByCall);
  const agentId = "12345";
  const timeZones = useSelector(getTimeZones);
  const isEditCommitment = useSelector(getIsEditCommitment);
  const isRescheduleCommitment = useSelector(getIsRescheduleCommitment);
  const notesRequiredToDelete = useSelector(getRemoveCommitmentSettings);
  const { delete: canDelete } = useSelector(getCommitmentPermission);
  const locale = useSelector(getApplicationLocale)

  const [timeZoneStandardName, setTimeZoneStandardName] = useState('');

  const { triggerRemoveToast } = useRemoveCommitment(
    notesRequiredToDelete,
    Number(formState.callbackId)
  );

  const placeHolderValue = translate('addNotes');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const phoneOBSkills: phoneOBSkills[] =[{ skillId: 0,skillName: "" }];
  useEffect(() => {
    if (!formState.dateTime) {
      handleDateChange(dayjs().add(1, 'hour').format('YYYY-MM-DDTHH:mm:ss'));
    }

    return () => {
      dispatch(commitmentActions.goBackToPreviousState());
      dispatch(commitmentActions.setIsEditCommitment(false));
      dispatch(commitmentActions.setIsRescheduleCommitment(false));
    };
  }, []);

  useEffect(() => {
    const activeCall = activeContacts.find(
      (contact : any) => contact.callType === CallType.REGULAR
    );
    if (activeCall && addCommitmentByCall) {
      const payload = {
        fieldName: 'phone',
        fieldValue: "inbound",
      };
      dispatch(commitmentActions.formData(payload));
    }
  }, [dispatch, addCommitmentByCall]);

  useEffect(() => {
    if (phoneOBSkills?.length > 0 && !formState.skillId) {
      const payload = {
        fieldName: 'skillId',
        fieldValue: String(phoneOBSkills[0]?.skillId),
      };
      dispatch(commitmentActions.formData(payload));
    }
  }, [phoneOBSkills, dispatch, formState?.skillId]);

  useEffect(() => {
    if (!timeZones.length) {
      //dispatch(fetchTimeZones());
      console.log("");
    }
    setTimeZoneStandardName(timeZones.find(item=>item.offset===formState.timeZone)?.standardName || '')
  }, [dispatch, timeZones]);

  const radioGroupOption = [
    {
      id: 'option1',
      value: translate('agent'),
      label: translate('agent'),
      isDisabled: isRescheduleCommitment,
      defaultSelected: true,
    },
    {
      id: 'option2',
      value: translate('skill'),
      label: translate('skill'),
      isDisabled: isRescheduleCommitment,
    }
  ];

  const isValidForm = useMemo(() => {
    return !!(
      formState.fname &&
      formState.lname &&
      formState.phone &&
      isValidPhoneNumber(formState.phone) &&
      !validateForm.dateTime
    );
  }, [formState,validateForm]);

  /**
   * Handle validation
   * @example validateCommitmentForm()
   */
  const validateCommitmentForm = (
    event:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent<string>
  ) => {
    const { name, value } = event.target;
    switch (name) {
    case 'phone': {
      setValidateForm({
        ...validateForm,
        [name]:
            !isValidPhoneNumber(value) || !value
              ? translate('phoneErrorMessage')
              : '',
      });
      break;
    }
    case 'fname': {
      setValidateForm({
        ...validateForm,
        [name]: value ? '' : translate('fnameErrorMessage'),
      });
      break;
    }
    case 'lname': {
      setValidateForm({
        ...validateForm,
        [name]: value ? '' : translate('lnameErrorMessage'),
      });
      break;
    }
    }
  };

  /**
   * Handle change is to handle form change event
   * @example handelChange()
   */
  const handleChange = (
    event:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent<string>
  ) => {
    validateCommitmentForm(event);

    if (event.target.name === 'timeZone') {
      const timeZoneOffset = String(
        timeZones.find(
          (item) =>
            item.standardName ===
            String((event.target as HTMLInputElement).value)
        )?.offset
      );
      validateDateTime(formState.dateTime, timeZoneOffset);
      const payload = {
        fieldName: event.target.name,
        fieldValue: timeZoneOffset,
      };
      dispatch(commitmentActions.formData(payload));
      setTimeZoneStandardName(event.target.value);
      return;
    }

    const payload = {
      fieldName: event.target.name,
      fieldValue: String((event.target as HTMLInputElement).value),
    };
    dispatch(commitmentActions.formData(payload));
  };

  /**
   *handleDateChange is to handle form change date event
   * @example handleDateChange()
   */
  const handleDateChange = (newValue: string) => {
    const payload = {
      fieldName: 'dateTime',
      fieldValue: newValue,
    };
    dispatch(commitmentActions.formData(payload));
  };

  /**
   * validate valid time, should not be less than current time
   * @example validateDateTime(dateTime)
   */
  const validateDateTime = (dateTime?: string, timeZoneOffset?: string) => {
    const updatedTimeZoneOffset = timeZoneOffset || formState.timeZone;
    const timeZoneCondition = updatedTimeZoneOffset.includes('-') ? '' : '+';
    const updatedDateString = dateTime || formState.dateTime;
    const updatedDateTimeWithTimezone = dayjs(
      new Date(updatedDateString + timeZoneCondition + updatedTimeZoneOffset)
    );

    const dateDiff = dayjs().diff(updatedDateTimeWithTimezone);
    setValidateForm({
      ...validateForm,
      dateTime: dateDiff > 0 ? translate('dateTimeErrorMessage') : '',
    });
  };

  /**
   *handleAddCommitment is to handle save form state
   * @example handleAddCommitment()
   */
  const handleAddCommitment = () => {
    validateDateTime(formState.dateTime);

    const timeZoneCondition = formState.timeZone.includes('-') ? '' : '+';

    const commitmentPayload: CommitmentRequest = {
      firstName: formState.fname,
      lastName: formState.lname,
      notes: formState.notes,
      phoneNumber: formState.phone,
      scheduleDate: formState.dateTime + timeZoneCondition + formState.timeZone,
      skillId: Number(formState.skillId),
      targetAgentId:
        formState.commitmentType === 'Skill' ? -1 : Number(agentId),
    };

    if (isValidForm && dayjs().diff(dayjs(formState.dateTime)) < 0) {
      // const isRescheduleCommitmentEnabled = isRescheduleCommitment
      //   ? rescheduleCommitment({
      //     callbackId: Number(formState.callbackId),
      //     rescheduleDate: commitmentPayload.scheduleDate,
      //   }) : createCommitment(commitmentPayload);
      // const handleSave = isEditCommitment
      //   ? editCommitment({
      //     callbackId: Number(formState.callbackId),
      //     commitmentPayload: commitmentPayload,
      //   }) : isRescheduleCommitmentEnabled
      //dispatch(handleSave);
      dispatch(commitmentActions.goBackToPreviousState());
      dispatch(commitmentActions.setIsEditCommitment(false));
      dispatch(commitmentActions.setIsRescheduleCommitment(false));
    }
  };

  const MenuProps = {
    PaperProps: {
      style: classes.selectOption,
    },
  };

  const isEditOrAddCommitment = isEditCommitment ? 'editCommitment' : 'addCommitments';
  const formTitle =  isRescheduleCommitment ? 'rescheduleCommitment' : isEditOrAddCommitment;

  return (
    <FormGroup sx={isFullView?classes.formWrapperLargeView:classes.formWrapper}>
      <CcfBox sx={classes.titleContainer}>
        <CcfBox sx={classes.iconContainer}>
          <CcfIconButton
            size={'small'}
            data-testid="go-back"
            onClick={() =>
              dispatch(commitmentActions.goBackToPreviousState())
            }
          >
            <CcfBackIcon />
          </CcfIconButton>
          <CcfTypography
            sx={classes.formLabelBold}
            translationKey={formTitle}
          />
        </CcfBox>
        <CcfDivider
          orientation={DividerOrientation.HORIZONTAL}
          variant={DividerVariant.FULLWIDTH}
          sx={classes.dividerStyles}
        />
      </CcfBox>

      <CcfBox sx={classes.formContainer}>
        <CcfBox sx={classes.formLabel}>
          <CcfRadioGroup
            data={radioGroupOption}
            onRadioButtonSelection={handleChange}
            name="commitmentType"
            horizontalAlign={true}
            defaultValue={translate('agent')}
            size="small"
          ></CcfRadioGroup>
        </CcfBox>

        <Select
          id="ccf-commitment-skill"
          value={formState?.skillId}
          onChange={handleChange}
          fullWidth
          name="skillId"
          size="small"
          sx={
            isRescheduleCommitment
              ? { ...classes.textStyles, ...classes.disabled }
              : classes.textStyles
          }
          disabled={isRescheduleCommitment}
          MenuProps={MenuProps}
        >
          {phoneOBSkills.map((skill) => (
            <MenuItem
              dense
              value={String(skill.skillId)}
              key={skill.skillId}
              sx={classes.menuItemStyle}
              title={skill.skillName}
            >
              {skill.skillName}
            </MenuItem>
          ))}
        </Select>

        <CcfDivider
          orientation={DividerOrientation.HORIZONTAL}
          variant={DividerVariant.FULLWIDTH}
          sx={classes.dividerStyles}
        />

        <CcfBox sx={{ width: '100%' }}>
          <CcfTypography sx={classes.formLabelBold} translationKey="customer" />
          <Grid container gap={'2%'} sx={{ paddingBottom: '5px' }}>
            <Grid item sx={classes.textFieldWrapper}>
              <CcfTypography
                sx={classes.labelFont}
                translationKey="firstName"
              />
              <CcfTextField
                name="fname"
                id="fname"
                size="small"
                onChange={handleChange}
                inputProps={{ 'data-testid': 'fname' }}
                sx={
                  isRescheduleCommitment
                    ? { ...classes.textFieldStyles, ...classes.disabled }
                    : classes.textFieldStyles
                }
                variant='outlined'
                error={!!validateForm.fname}
                helperText={validateForm.fname}
                onBlur={validateCommitmentForm}
                value={formState.fname}
                FormHelperTextProps={{ style: classes.helperTextStyles }}
                disabled={isRescheduleCommitment}
                aria-label = {translate('firstName')}
              />
            </Grid>

            <Grid item sx={classes.textFieldWrapper}>
              <CcfTypography sx={classes.labelFont} translationKey="lastName" />
              <CcfTextField
                name="lname"
                id="lname"
                size="small"
                onChange={handleChange}
                inputProps={{ 'data-testid': 'lname' }}
                sx={
                  isRescheduleCommitment
                    ? { ...classes.textFieldStyles, ...classes.disabled }
                    : classes.textFieldStyles
                }
                variant='outlined'
                error={!!validateForm.lname}
                helperText={validateForm.lname}
                onBlur={validateCommitmentForm}
                value={formState.lname}
                FormHelperTextProps={{ style: classes.helperTextStyles }}
                disabled={isRescheduleCommitment}
                aria-label = {translate('lastName')}
              />
            </Grid>
          </Grid>
          <CcfBox>
            <CcfTypography
              sx={classes.labelFont}
              translationKey="contactInformation"
            />

            <CcfBox sx={{ display: 'flex', gap: '2%' }}>
              <CcfTypography
                sx={classes.contactStyles}
                translationKey="phone"
              />
              <CcfTextField
                name="phone"
                id="phone"
                size="small"
                inputProps={{ 'data-testid': 'phone' }}
                sx={
                  isRescheduleCommitment
                    ? { ...classes.contactTextFieldStyles, ...classes.disabled }
                    : classes.contactTextFieldStyles
                }
                variant='outlined'
                onChange={handleChange}
                error={!!validateForm.phone}
                helperText={validateForm.phone}
                onBlur={validateCommitmentForm}
                value={formState.phone}
                FormHelperTextProps={{ style: classes.helperTextStyles }}
                disabled={isRescheduleCommitment}
                aria-label= {translate('phone')}
              />
            </CcfBox>
          </CcfBox>

          <CcfBox sx={classes.wrappperContainer}>
            <CcfTypography
              sx={classes.formLabelBoldRequired}
              translationKey="time"
            />
            <LocalizationProvider
              dateAdapter={AdapterDayjs}
              adapterLocale={locale}
            >
              <CcfBox data-testid="date-input">
                <DateTimePicker
                  value={dayjs(formState.dateTime)}
                  onChange={(value:any) => {
                    const newDate = value?.format('YYYY-MM-DDTHH:mm:ss');
                    newDate && handleDateChange(newDate);
                    validateDateTime(newDate);
                  }}
                  key={locale}
                  renderInput={(params : any) => (
                    <CcfTextField
                      {...params}
                      id="date-time"
                      fullWidth
                      size="small"
                      sx={classes.datePicker}
                      variant='outlined'
                      onChange={() => validateDateTime(formState.dateTime)}
                      onBlur={() => validateDateTime(formState.dateTime)}
                      error={!!validateForm.dateTime}
                      helperText={validateForm.dateTime}
                      FormHelperTextProps={{ style: classes.helperTextStyles }}
                    />
                  )}
                  PopperProps={{
                    placement: 'auto',
                  }}
                  components={{
                    OpenPickerIcon: DatePickerIcon,
                  }}
                  disablePast
                  minDateTime={dayjs()}
                />
              </CcfBox>
            </LocalizationProvider>
          </CcfBox>

          <CcfBox sx={classes.wrappperContainer}>
            <CcfTypography
              sx={classes.formLabelBold}
              translationKey="timeZone"
            />
            <Select
              id="time-zone"
              inputProps={{ 'data-testid': 'timeZones' }}
              value={timeZoneStandardName}
              onChange={handleChange}
              fullWidth
              name="timeZone"
              size="small"
              sx={classes.timeZoneStyles}
              MenuProps={MenuProps}
            >
              {timeZones.map((data) => (
                <MenuItem
                  dense
                  value={data.standardName}
                  key={data.displayName}
                  sx={classes.menuItemStyle}
                  title={data.displayName}
                >
                  {data.displayName}
                </MenuItem>
              ))}
            </Select>
          </CcfBox>

          <CcfBox sx={classes.wrappperContainer}>
            <CcfTypography sx={classes.formLabelBold} translationKey="notes" />

            <CcfTextField
              name="notes"
              id="notes"
              fullWidth
              onChange={handleChange}
              placeholder={placeHolderValue}
              multiline
              variant='outlined'
              sx={
                isRescheduleCommitment
                  ? { ...classes.textAreaStyles, ...classes.disabled }
                  : classes.textAreaStyles
              }
              value={formState.notes}
              disabled={isRescheduleCommitment}
            />
          </CcfBox>
        </CcfBox>

        <CcfBox sx={classes.buttonWrapper}>
          <CcfButton
            onClick={() => dispatch(commitmentActions.goBackToPreviousState())}
            data-testid="cancel"
            aria-label= {translate('cancel')}
          >
            <CcfTypography translationKey="cancel" sx={classes.textStyles} />
          </CcfButton>
          <CcfButton
            primary
            onClick={handleAddCommitment}
            data-testid="add"
            disabled={!isValidForm}
            aria-label= {translate('add')}
          >
            <CcfTypography translationKey="save" sx={classes.textStyles} />
          </CcfButton>
          {isEditCommitment && canDelete && (
            <CcfButton
              primary
              onClick={triggerRemoveToast}
              data-testid="remove"
              disabled={!isValidForm}
              aria-label= {translate('remove')}
            >
              <CcfTypography translationKey="remove" sx={classes.textStyles} />
            </CcfButton>
          )}
        </CcfBox>
      </CcfBox>
    </FormGroup>
  );
};
